import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './StudentRightPanel.module.css';
import DiscussionPulsePanel from '../discussion-pulse/DiscussionPulsePanel';
import { createVoiceprintCapture } from '../../services/voiceprint-capture';

const MAX_RECORD_MS = 20000;

export default function StudentRightPanel({ currentUser, sessionDeviceId, onUserUpdate }) {
    // Two columns inside the panel:
    //   left  (50%): Discussion Pulse, spans the full panel height.
    //   right (50%): greeting, Join Discussion, Voiceprint — stacked top-to-bottom.
    const greetingName = currentUser?.display_name || currentUser?.email;
    return (
        <div className={styles.panel}>
            <div className={styles.leftColumn}>
                <ActiveDiscussionSection sessionDeviceId={sessionDeviceId} />
            </div>
            <div className={styles.rightColumn}>
                {greetingName && (
                    <div className={styles.greeting}>Hello, {greetingName}!</div>
                )}
                <JoinDiscussionSection />
                <VoiceprintSection currentUser={currentUser} onUserUpdate={onUserUpdate} />
            </div>
        </div>
    );
}

function ActiveDiscussionSection({ sessionDeviceId }) {
    // Treat the pulse area as another artifact: blank when no session is
    // selected, mirroring how Assessment / Concept Map look in the
    // ContentArea before the user picks a session from the navigator.
    return (
        <div className={`${styles.section} ${styles.pulseSection}`}>
            <div className={styles.sectionTitle}>Discussion Pulse</div>
            {sessionDeviceId && (
                <div className={styles.pulseEmbed}>
                    <DiscussionPulsePanel
                        sessionDeviceId={sessionDeviceId}
                        isSessionActive={true}
                        isCollapsed={false}
                        onToggleCollapse={() => {}}
                        fill={true}
                    />
                </div>
            )}
        </div>
    );
}

function JoinDiscussionSection() {
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    // Two explicit join buttons instead of regex-detecting the code's role.
    // Recorder passcodes (TA) and viewer codes (recorder) can both be all
    // digits, so implicit detection has a real misclassification edge case;
    // making the user pick removes that ambiguity.
    //
    // Voiceprint precondition: a student joining as recorder MUST already
    // have a voiceprint enrolled. Without one, the auto-push pipeline on
    // the join page has nothing to push, and the legacy speaker-enrollment
    // gate strands them on a screen they shouldn't see. We surface the
    // failure here, before navigating, so the user can act on it.
    const ensureVoiceprintExists = async () => {
        try {
            const res = await fetch('/api/v1/me/voiceprint');
            if (!res.ok) return false;
            const data = await res.json();
            return !!(data && data.voiceprint);
        } catch (_) {
            return false;
        }
    };

    const joinAsRecorder = async () => {
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) { setError('Enter the code from your TA.'); return; }
        const groupName = (name || '').trim();
        if (!groupName) { setError('Enter your group name.'); return; }
        setError(null);
        setBusy(true);
        try {
            const ok = await ensureVoiceprintExists();
            if (!ok) {
                setError('Record your voiceprint first — see "Your Voice" below — then click Join as Recorder again.');
                return;
            }
            // Stash the join intent in sessionStorage instead of the URL —
            // putting passcode/name in the address bar looks unprofessional
            // and leaks them into browser history. byod-join-component reads
            // these on mount with the same precedence as URL params used to
            // have. Cleared after successful join (existing logic).
            try {
                sessionStorage.setItem('cs330_pending_passcode', trimmed);
                sessionStorage.setItem('cs330_pending_name', groupName);
            } catch (_) {}
            navigate('/join');
        } finally {
            setBusy(false);
        }
    };

    const joinAsViewer = async () => {
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) { setError('Enter the code from your recorder.'); return; }
        setError(null);
        setBusy(true);
        try {
            // Same gate as recorder. Without a voiceprint, the viewer's
            // utterances captured on the recorder's mic won't be tagged with
            // their display_name; they'll show as "Speaker N" instead.
            const ok = await ensureVoiceprintExists();
            if (!ok) {
                setError('Record your voiceprint first — see "Your Voice" below — then click Join as Viewer again.');
                return;
            }
            try {
                sessionStorage.setItem('cs330_pending_viewer_code', trimmed);
            } catch (_) {}
            navigate('/viewer');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={`${styles.section} ${styles.joinSection}`}>
            <div className={styles.sectionTitle}>Join Discussion</div>
            <input
                className={`${styles.input} ${styles.codeInput}`}
                placeholder="CODE"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\s/g, '').toUpperCase())}
                maxLength={8}
            />
            <input
                className={styles.input}
                placeholder="Group name (recorder only)"
                value={name}
                onChange={e => setName(e.target.value)}
                style={{ marginTop: 8 }}
            />
            <div className={styles.joinRow}>
                <button
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    onClick={joinAsRecorder}
                    disabled={busy}
                >
                    {busy ? 'Checking…' : 'Join as Recorder'}
                </button>
                <button
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    onClick={joinAsViewer}
                    disabled={busy}
                >
                    {busy ? 'Checking…' : 'Join as Viewer'}
                </button>
            </div>
            {error && <div className={styles.statusError} style={{ marginTop: 8 }}>{error}</div>}
        </div>
    );
}

function VoiceprintSection({ currentUser, onUserUpdate }) {
    const [voiceprint, setVoiceprint] = useState(null);
    const [vpError, setVpError] = useState(null);
    const [recording, setRecording] = useState(false);
    const [uploading, setUploading] = useState(false);
    const recordRef = useRef({ cap: null, timer: null });
    const audioElRef = useRef(null);

    useEffect(() => {
        refreshVoiceprint();
    }, []);

    async function refreshVoiceprint() {
        try {
            const res = await fetch('/api/v1/me/voiceprint');
            if (!res.ok) return;
            const data = await res.json();
            setVoiceprint(data.voiceprint);
        } catch (_) {}
    }

    async function startRecording() {
        setVpError(null);
        try {
            const cap = createVoiceprintCapture();
            await cap.start();
            recordRef.current = { cap, timer: null };
            setRecording(true);
            recordRef.current.timer = setTimeout(() => stopRecording(true), MAX_RECORD_MS);
        } catch (e) {
            setVpError('Microphone access denied or unavailable: ' + (e.message || e));
        }
    }

    async function stopRecording(autoUpload) {
        const ref = recordRef.current;
        if (ref.timer) clearTimeout(ref.timer);
        const merged = ref.cap ? ref.cap.stop() : new Float32Array(0);
        setRecording(false);
        const total = merged.length;
        if (total < 16000 * 1) {
            setVpError('Recording too short — at least 1 second is required.');
            return;
        }

        setUploading(true);
        try {
            const res = await fetch('/api/v1/me/voiceprint', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'X-Audio-Encoding': 'pcm_f32le',
                    'X-Audio-Sample-Rate': '16000',
                    'X-Audio-Duration-Ms': String(Math.round(total / 16)),
                },
                body: merged.buffer,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                setVpError(err.message || 'Upload failed.');
            } else {
                const data = await res.json();
                setVoiceprint(data.voiceprint);
            }
        } catch (e) {
            setVpError('Upload failed: ' + (e.message || e));
        } finally {
            setUploading(false);
        }
    }

    async function deleteVoiceprint() {
        if (!window.confirm('Delete your voiceprint? You can re-record any time.')) return;
        const res = await fetch('/api/v1/me/voiceprint', { method: 'DELETE' });
        if (res.ok) setVoiceprint(null);
    }


    function playVoiceprint(url) {
        // Reuse the same hidden <audio> element across plays so a quick
        // re-click stops the previous playback before starting the next.
        if (!audioElRef.current) {
            audioElRef.current = new Audio();
        }
        const el = audioElRef.current;
        try { el.pause(); } catch (_) {}
        // Cache-bust so a freshly re-recorded voiceprint plays the new audio
        // instead of a stale browser-cached blob.
        el.src = `${url}?t=${Date.now()}`;
        el.play().catch(e => {
            setVpError('Could not play recording: ' + (e.message || e));
        });
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionTitle}>Your Voice</div>

            <div className={styles.fieldLabel}>Voiceprint</div>
            {voiceprint ? (
                <div className={styles.statusOk}>
                    ● Recorded {voiceprint.created_at?.slice(0, 16) || ''}
                    {voiceprint.duration_ms ? ` (${(voiceprint.duration_ms / 1000).toFixed(1)}s)` : ''}
                </div>
            ) : (
                <div className={styles.statusEmpty}>○ Not recorded yet</div>
            )}

            {recording ? (
                <button className={`${styles.recordButton} ${styles.recording}`} onClick={() => stopRecording(true)}>
                    <span className={styles.dot} /> Stop &amp; Save
                </button>
            ) : (
                <button
                    className={styles.recordButton}
                    onClick={startRecording}
                    disabled={uploading}
                    style={{ marginTop: 8 }}
                >
                    {uploading ? 'Uploading…' : voiceprint ? 'Re-record' : 'Record now'}
                </button>
            )}

            <div className={styles.helperText}>
                Speak naturally for about 12 seconds in a quiet space.
            </div>

            {voiceprint && (
                <div className={styles.row} style={{ marginTop: 10 }}>
                    <button className={styles.btn} onClick={() => playVoiceprint('/api/v1/me/voiceprint/audio')}>
                        ▶ Play
                    </button>
                    <button className={`${styles.btn} ${styles.btnDanger}`} onClick={deleteVoiceprint}>Delete</button>
                </div>
            )}

            {vpError && <div className={styles.statusError} style={{ marginTop: 8 }}>{vpError}</div>}
        </div>
    );
}
